import { ApolloError } from '@apollo/client';
import { FormikHandlers, FormikHelpers, FormikState } from 'formik';
import slugify from 'slugify';

import { VARIANT_NAME_MAX_CHAR } from 'src/app/graph/graphSettingsPage/variantsPage/addVariantWizard/shared';
import { CLOUD_ROUTER_ENDPOINT_DOMAIN_NAME } from 'src/app/onboarding/constants';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import {
  API_NAME_MAX_CHAR,
  GRAPH_ID_MAX_CHAR,
  GRAPH_NAME_MAX_CHAR,
} from 'src/lib/graphOnboarding/constants';
import {
  CloudProvider,
  CloudTier,
  PrivateSubgraphShareStatus,
  PrivateSubgraphSync,
  PrivateSubgraphsQuery,
  RegionDescription,
} from 'src/lib/graphqlTypes/types';
import { isValidUrl } from 'src/lib/isValidUrl';
import { PUBLIC_API_ENDPOINT_URL_SCHEMA, Yup } from 'src/lib/yup';

import { FlowTypes } from './FlowTypeProvider';

// Functions, enums, and constants to render step names in the stepper

// Enums for onboarding steps specific to each type of flow
export enum SupergraphOnboardingStepPrivate {
  YourPlan = 'Choose Your Plan',
  AddAWSAccount = 'Connect Your Private Resource',
  YourSharedResource = 'Connect Your Service',
  YourGraphQLAPI = 'Create Your Router',
  Supergraph = 'Success',
}

export enum SupergraphOnboardingStepPublic {
  YourGraphQLAPI = 'GraphQL Endpoint',
  DedicatedProvider = 'Configure Your Service',
  Supergraph = 'Create Your Supergraph',
}

export enum SupergraphOnboardingStep {
  YourGraphQLAPI = 'Your GraphQL API',
  Supergraph = 'Supergraph',
}

// Array of default Serverless onboarding steps
export const SupergraphOnboardingSteps = [
  SupergraphOnboardingStep.YourGraphQLAPI,
  SupergraphOnboardingStep.Supergraph,
];

// Information on the private url resource from an
// AWS account that is being used to create a private subgraph
export type UrlInfo = {
  domainUrl: string | null;
  name: string | null;
  region: Pick<RegionDescription, 'code' | 'name'>;
  status: PrivateSubgraphShareStatus;
};

export const getSupergraphOnboardingStepStrings = (
  flowType: FlowTypes,
  hasPrivateSubgraphs?: boolean,
): string[] => {
  let enumValues: string[];

  switch (flowType) {
    case 'DEDICATED_PRIVATE':
      enumValues = Object.values(SupergraphOnboardingStepPrivate);
      break;
    case 'DEDICATED_PUBLIC':
      enumValues = Object.values(SupergraphOnboardingStepPublic);
      break;
    case 'SERVERLESS':
      enumValues = Object.values(SupergraphOnboardingStep);
      break;
    default:
      return assertUnreachable(flowType);
  }

  return enumValues.map((step) => {
    if (step === SupergraphOnboardingStepPrivate.AddAWSAccount) {
      return hasPrivateSubgraphs
        ? 'Private Resources Found'
        : 'Connect Your Private Resource';
    }
    return step;
  });
};

export const DEFAULT_VARIANT_NAME = 'main';

/**
 * This function should be used when auto generating graph ids to avoid generating
 * invalid ids when we aren't displaying id errors.
 */
export function createValidSupergraphId(title: string, uniqueString?: string) {
  let slugifiedTitle = slugify(title, { strict: true }).replace(
    /^[^a-zA-Z]*/,
    '',
  );
  // we remove 'supergraph' from the end of the id if it exists & its too long
  if (
    slugifiedTitle.length + (uniqueString ? uniqueString.length + 1 : 0) >
    GRAPH_ID_MAX_CHAR
  ) {
    slugifiedTitle = slugifiedTitle.replace('-supergraph', '');
  }
  return slugify(
    `${slugifiedTitle.slice(
      0,
      GRAPH_ID_MAX_CHAR - (uniqueString ? uniqueString.length + 1 : 0),
    )}${slugifiedTitle.length < 1 ? 'id' : ''}${
      uniqueString ? `-${uniqueString}` : ''
    }`,
    { strict: true },
  );
}

export const getRouterEndpoint = ({
  variantName,
  supergraphID,
}: {
  variantName: string;
  supergraphID: string;
}) => {
  // TODO: Query CLOUD_ROUTER_ENDPOINT_DOMAIN_NAME from backend when avaiilable.
  return `https://${variantName}--${supergraphID}.${CLOUD_ROUTER_ENDPOINT_DOMAIN_NAME}/graphql`;
};

export type existingGraphVariants = {
  graphVariantRef: string;
  graphName: string;
  variantName: string;
  cloudRegion: Pick<RegionDescription, 'code' | 'name'>;
}[];

export type CommonCloudDedicatedProps = {
  isDedicatedPlan: boolean;
  flowType: FlowTypes;
  createCloudOnboardingError: ApolloError | undefined;
  availableUnregisteredResources: UrlInfo[];
  setAvailableUnregisteredResources: React.Dispatch<
    React.SetStateAction<UrlInfo[]>
  >;
  prefillGraphRef: string | undefined;
  existingGraphVariants: existingGraphVariants | undefined;
  privateSubgraphsData: PrivateSubgraphsQuery | undefined;
  privateSubgraphsLoading: boolean;
  setShouldQueryPrivateSubgraphs: React.Dispatch<React.SetStateAction<boolean>>;
  sharedResources: PrivateSubgraphSync | null | undefined;
  sharedResourcesLoading: boolean;
  sharedResourcesError: ApolloError | undefined;
  setHasPrivateSubgraphs: React.Dispatch<React.SetStateAction<boolean>>;
  selectedPrivateDomainName: string | null;
  setSelectedPrivateDomainName: React.Dispatch<React.SetStateAction<string>>;
};

export type OnboardingLayoutProps = {
  previousStep: number;
  setPreviousStep: React.Dispatch<React.SetStateAction<number>>;
  hasPrivateSubgraphs: boolean;
};

export type CommonStepProps = {
  createGraphError: ApolloError | undefined;
  createGraphValidationError: string | undefined;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  errors: FormikState<NewSupergraphOnboardingFields>['errors'];
  handleBlur: FormikHandlers['handleBlur'];
  setFieldValue: FormikHelpers<NewSupergraphOnboardingFields>['setFieldValue'];
  touched: FormikState<NewSupergraphOnboardingFields>['touched'];
  setFieldTouched: FormikHelpers<NewSupergraphOnboardingFields>['setFieldTouched'];
  values: FormikState<NewSupergraphOnboardingFields>['values'];
  validateField: FormikHelpers<NewSupergraphOnboardingFields>['validateField'];
  setValues: FormikHelpers<NewSupergraphOnboardingFields>['setValues'];
  creatingSupergraph: boolean;
  createSupergraph: () => void;
  continueAnalyticsLabelsByStep: Record<SupergraphOnboardingStep, string>;
  whenStepsBegan: Partial<Record<SupergraphOnboardingStep, number>> | undefined;
  setWhenStepsBegan: React.Dispatch<
    React.SetStateAction<
      Partial<Record<SupergraphOnboardingStep, number>> | undefined
    >
  >;
};

export type NewSupergraphOnboardingFields = Yup.InferType<
  typeof newSupergraphOnboardingValidationSchema
>;

export const getSubgraphNameFromGraphVariant = ({
  graphTitle,
  variantName,
}: {
  graphTitle: string;
  variantName: string;
}) => {
  return `${slugify(graphTitle, { strict: true })}-${variantName}`;
};

// Error messages for the form
export const newSupergraphOnboardingErrorMessages = {
  endpointUrl: {
    required: 'Please provide an endpoint url.',
    validUrl: 'Please enter a valid URL',
    localUrl:
      'Endpoints need to be available on the public internet. Please deploy your API before continuing.',
  },
  subgraphName: {
    required: 'Please provide a name for your Subgraph.',
    min: 'API name must be at least 2 characters.',
    max: `API name must be at most ${API_NAME_MAX_CHAR} characters.`,
    startWithLetter: 'API name must start with a letter.',
    contents: 'API name may only contain letters, numbers, and dashes.',
  },
  supergraphName: {
    required: 'Please provide a name for your supergraph.',
    max: `Supergraph name must be at most ${GRAPH_NAME_MAX_CHAR} characters.`,
    min: 'Supergraph name must be at least 2 characters.',
  },
  supergraphID: {
    required: 'Please provide an id for your supergraph.',
    start: 'Supergraph ID name must start with a letter.',
    max: `Supergraph ID must be at most ${GRAPH_ID_MAX_CHAR} characters.`,
    min: 'Supergraph ID must be at least 2 characters.',
    acceptedChars:
      'Supergraph ID may only contain letters, numbers, and dashes.',
    doubleDash: 'Supergraph ID may not contain two consecutive dashes.',
  },
  sdl: {
    invalid: 'Please enter a valid GraphQL SDL',
  },
  selectedRegisteredVariant: {
    defined: 'Please select a registered variant',
  },
  arn: 'Please enter a valid ARN',
  variantName: {
    min: 'Supergraph ID must be at least 2 characters.',
    max: `Variant name must be at most ${VARIANT_NAME_MAX_CHAR} characters.`,
  },
};

// Define AWS ARN regex pattern as a constant.
// An AWS ARN (Amazon Resource Name) is a unique identifier
// for resources in AWS, used to specify and locate a resource
// across all of AWS services. It is used in GraphOS to identify
// a list of a user's private GraphQL endpoints on AWS.
const ARN_REGEX_PATTERN =
  /^arn:aws:ram:[a-z0-9-]+:[0-9]{12}:resource-share\/[a-zA-Z0-9-]+$/;

export const isUrlCompatibleWithArnPattern = (url: string): boolean => {
  if (!url) return false;

  try {
    const urlObj = new URL(url);

    // Check if the URL hostname includes 'aws' and 'ram'
    // and if the pathname includes 'resource-share'
    if (
      urlObj.hostname.includes('aws') &&
      urlObj.hostname.includes('ram') &&
      urlObj.pathname.includes('resource-share')
    ) {
      return true;
    }

    // Check if the URL is a VPC lattice AWS URL
    if (urlObj.hostname.endsWith('.vpc-lattice-svcs.us-east-1.on.aws')) {
      return true;
    }

    return false;
  } catch (error) {
    return false; // Invalid URL
  }
};

const subgraphNameSchema = Yup.string()
  .required(newSupergraphOnboardingErrorMessages.subgraphName.required)
  .min(2, newSupergraphOnboardingErrorMessages.subgraphName.min)
  .max(API_NAME_MAX_CHAR, newSupergraphOnboardingErrorMessages.subgraphName.max)
  .matches(
    /^[a-zA-Z]/,
    newSupergraphOnboardingErrorMessages.subgraphName.startWithLetter,
  )
  .matches(
    /^[a-zA-Z0-9_-]*$/,
    newSupergraphOnboardingErrorMessages.subgraphName.contents,
  );

/**
 * Schema for creating a new supergraph
 */
export const newSupergraphOnboardingValidationSchema = Yup.object().shape({
  tab: Yup.string().oneOf(['unregistered', 'registered']),
  // This sdl is the introspected document, manual sdl upload or sdl from the registry.
  // For a subgraph, this will include all federation directives
  sdl: Yup.string().required(),
  // the below 5 fields are for the first step. We require either both
  // unregistered fields or both registered fields depending on the tab you are on!
  unregisteredEndpointUrl: Yup.string().when('tab', (tab) =>
    tab === 'unregistered' ? PUBLIC_API_ENDPOINT_URL_SCHEMA : Yup.string(),
  ),
  unregisteredSubgraphName: Yup.string().when('tab', (tab) =>
    tab === 'unregistered' ? subgraphNameSchema : Yup.string(),
  ),
  registeredGraphRef: Yup.string().when('tab', (tab) =>
    tab === 'registered'
      ? Yup.string()
          .required(newSupergraphOnboardingErrorMessages.subgraphName.required)
          .min(2)
      : Yup.string(),
  ),
  registeredVariantSubgraphName: Yup.string().when('tab', (tab) =>
    tab === 'registered' ? subgraphNameSchema : Yup.string(),
  ),
  registeredEndpointUrl: Yup.string().when('tab', (tab) =>
    tab === 'registered'
      ? Yup.string()
          .required(newSupergraphOnboardingErrorMessages.endpointUrl.required)
          .test(
            'valid-http-url',
            newSupergraphOnboardingErrorMessages.endpointUrl.validUrl,
            (value: string) => {
              return !value || isValidUrl(value);
            },
          )
      : Yup.string(),
  ),
  supergraphName: Yup.string()
    .required(newSupergraphOnboardingErrorMessages.supergraphName.required)
    .min(2, newSupergraphOnboardingErrorMessages.supergraphName.min)
    .max(
      GRAPH_NAME_MAX_CHAR,
      newSupergraphOnboardingErrorMessages.supergraphName.max,
    ),

  supergraphID: Yup.string()
    .required(newSupergraphOnboardingErrorMessages.supergraphID.required)
    .min(2, newSupergraphOnboardingErrorMessages.supergraphID.min)
    .max(
      GRAPH_ID_MAX_CHAR,
      newSupergraphOnboardingErrorMessages.supergraphID.max,
    )
    .matches(
      /^[a-zA-Z]/,
      newSupergraphOnboardingErrorMessages.supergraphID.start,
    )
    .matches(
      /^[a-zA-Z0-9-]*$/,
      newSupergraphOnboardingErrorMessages.supergraphID.acceptedChars,
    )
    .matches(
      /^(?!.*\-\-)[a-zA-Z0-9-]*$/,
      newSupergraphOnboardingErrorMessages.supergraphID.doubleDash,
    ),
  cloudProvider: Yup.mixed().oneOf(Object.values(CloudProvider)),
  cloudTier: Yup.mixed().oneOf(Object.values(CloudTier)),
  cloudRegion: Yup.string(),
  arn: Yup.string().matches(
    ARN_REGEX_PATTERN,
    newSupergraphOnboardingErrorMessages.arn,
  ),
  variantName: Yup.string()
    .min(2, newSupergraphOnboardingErrorMessages.variantName.min)
    .max(
      VARIANT_NAME_MAX_CHAR,
      newSupergraphOnboardingErrorMessages.variantName.max,
    ),
});
